import './Hubspotform.css'

import Cookies from 'js-cookie'
import { useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'

export type HubspotformProps = {
  title: string
  formId: string
  portalId?: string
  region?: string
  height?: string
  width?: string
  anchorTag?: string
  hiddenFields?: { [field: string]: string }
  accessCookie?: string
}

export const PhnxHubspotform: React.FC<HubspotformProps> = ({
  formId,
  portalId = '441838444',
  region = 'ap1',
  height = 'auto',
  width = 'auto',
  anchorTag,
  hiddenFields = {},
  accessCookie,
}) => {
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' })

  useEffect(() => {
    const timeout = setTimeout(() => {
      const script = document.createElement('script')
      const scriptSrc = `https://js-${region}.hsforms.net/forms/embed/developer/${portalId}.js`

      script.src = scriptSrc
      script.defer = true
      document.body.appendChild(script)
    }, 500)

    return () => clearTimeout(timeout)
  }, [formId, portalId, region])

  // updating the width on the form
  useEffect(() => {
    if (!formWrapperRef.current) return

    const applyMaxWidth = () => {
      const rendererDiv = formWrapperRef.current?.querySelector<HTMLDivElement>(
        'div[data-hsfc-id="Renderer"]'
      )
      if (rendererDiv) {
        rendererDiv.style.maxWidth = isDesktop ? width || '100%' : 'auto'
        rendererDiv.style.display = isDesktop ? 'flex' : 'block'
        console.warn('🟢 HubSpot Renderer max-width applied:', rendererDiv.style.maxWidth)
      }
    }

    // Initial application
    applyMaxWidth()

    // Observe DOM mutations for late-injected renderer
    const observer = new MutationObserver(() => applyMaxWidth())
    observer.observe(formWrapperRef.current, { childList: true, subtree: true })

    // Listen for resize events
    window.addEventListener('resize', applyMaxWidth)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', applyMaxWidth)
    }
  }, [width, isDesktop])

  useEffect(() => {
    const handleFormReady = (event: any) => {
      const form = (window as any).HubSpotFormsV4?.getFormFromEvent?.(event)
      if (form) {
        Object.entries(hiddenFields).forEach(([key, value]) => {
          try {
            form.setFieldValue(key, value)
            console.warn(`set field called ${key}`)
          } catch {
            console.warn(`Could not set field ${key}`)
          }
        })
      }
    }

    window.addEventListener('hs-form-event:on-ready', handleFormReady)
    return () => window.removeEventListener('hs-form-event:on-ready', handleFormReady)
  }, [hiddenFields])

  const formWrapperRef = useRef<HTMLDivElement>(null)
  const formDataCaptureRef = useRef<{ [key: string]: string }>({})

  // --- CAPTURE FORM DATA ON FORM CHANGE (NON-INTRUSIVE) ---
  useEffect(() => {
    const formElement = formWrapperRef.current
    if (!formElement) return

    const observer = new MutationObserver(() => {
      const form = formElement.querySelector('form')
      if (form && !form.dataset.listenerAttached) {
        form.dataset.listenerAttached = 'true'

        // Listen for input changes to capture data without interfering with submission
        const captureFormData = () => {
          const formData = new FormData(form)
          const formDataObject: { [key: string]: string } = {}

          formData.forEach((value, key) => {
            if (typeof value === 'string') {
              formDataObject[key] = value
            }
          })

          formDataCaptureRef.current = formDataObject
          console.warn('📝 Form data captured (non-intrusive):', formDataObject)
        }

        // Capture on any input change
        form.addEventListener('change', captureFormData)
        form.addEventListener('input', captureFormData)
      }
    })

    observer.observe(formElement, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  // --- ON-SUCCESS EVENT HANDLER ---
  useEffect(() => {
    const handleFormSuccess = (event: any) => {
      console.warn('🟢 HubSpot form submit confirmed (SUCCESSFUL)', event)

      // Set the form cookie for gated content access (after successful submission)
      Cookies.set('form-cookie', accessCookie ?? 'true', { expires: 1 / 24 })
      console.warn('🍪 Form cookie set for gated content access')

      // Use the captured form data from the ref
      const formData = formDataCaptureRef.current
      console.warn('📊 Form data from capture:', formData)

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'hubspotFormSubmitSuccess',
        formId: formId,
        formData: formData,
      })
      console.warn('📤 Pushed to dataLayer:', {
        event: 'hubspotFormSubmitSuccess',
        formId,
        formData,
      })
    }

    // Using the official success event
    window.addEventListener('hs-form-event:on-submission:success', handleFormSuccess)

    return () =>
      window.removeEventListener('hs-form-event:on-submission:success', handleFormSuccess)
  }, [formId, accessCookie])

  return (
    <div
      id={anchorTag || undefined}
      ref={formWrapperRef}
      className="hs-form-html"
      data-region={region}
      data-form-id={formId}
      data-portal-id={portalId}
      style={{
        minWidth: '100%',
        maxWidth: '100%',
        minHeight: height,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  )
}
